import { useQuery } from '@tanstack/react-query'
import { api } from '../../lib/api'
import type { TenantAtual } from '../../types'

interface ContagemResponse {
  count: number
}

/**
 * 4 etapas do onboarding (decisão P2 — 2026-05-04):
 *   1. PerfilTenant configurado
 *   2. Pelo menos 1 Empresa Cliente cadastrada
 *   3. Pelo menos 1 upload de planilha OU 1 área+equipamento manual
 *   4. Pelo menos 1 PMOC emitido
 *
 * Cada passo é "feito" ou "pendente". Sem persistência client-side; o
 * backend é a fonte da verdade.
 */
export function useOnboardingProgress() {
  return useQuery({
    queryKey: ['onboarding-progress-v2'],
    queryFn: async () => {
      const [tenant, clientesEmpresas, equipamentos, pmocs] = await Promise.all([
        api.get<TenantAtual>('/tenants/atual/'),
        api.get<ContagemResponse>('/empresas/', {
          params: { page_size: 1, papeis: 'CLIENTE' },
        }),
        api.get<ContagemResponse>('/equipamentos/', { params: { page_size: 1 } }),
        api.get<ContagemResponse>('/pmoc/', { params: { page_size: 1 } }),
      ])

      const tenantData = tenant.data
      const tenantConfigurado = Boolean(
        tenantData.nome &&
          tenantData.cnpj &&
          tenantData.email_contato,
      )

      return {
        perfil_tenant: tenantConfigurado,
        clientes: clientesEmpresas.data.count ?? 0,
        equipamentos: equipamentos.data.count ?? 0,
        pmocs: pmocs.data.count ?? 0,
      }
    },
    staleTime: 1000 * 30,
  })
}

export interface PassoOnboarding {
  id: string
  titulo: string
  descricao: string
  ctaTo: string
  ctaLabel: string
  feito: boolean
}

export function calcularPassos(
  progresso: NonNullable<ReturnType<typeof useOnboardingProgress>['data']>,
): PassoOnboarding[] {
  return [
    {
      id: 'perfil-tenant',
      titulo: '1. Configure os dados da sua empresa',
      descricao:
        'Razão social, CNPJ, email de contato. É a sua identidade no sistema — vai aparecer em PMOCs e ordens de serviço.',
      ctaTo: '/configuracoes',
      ctaLabel: 'Abrir configurações',
      feito: progresso.perfil_tenant,
    },
    {
      id: 'cliente',
      titulo: '2. Cadastre o primeiro cliente (contratante)',
      descricao:
        'A empresa contratante (dona dos equipamentos). Cada PMOC pertence a um cliente.',
      ctaTo: '/empresas/novo?papel=CLIENTE',
      ctaLabel: '+ Novo cliente',
      feito: progresso.clientes > 0,
    },
    {
      id: 'equipamentos',
      titulo: '3. Cadastre equipamentos (planilha ou manual)',
      descricao:
        'Splits, chillers, UTAs, fancoils. Você pode importar uma planilha (rápido se já tem inventário) ou cadastrar manualmente (1-10 equipamentos).',
      ctaTo: '/onboarding/equipamentos',
      ctaLabel: 'Escolher caminho',
      feito: progresso.equipamentos > 0,
    },
    {
      id: 'pmoc',
      titulo: '4. Crie o primeiro PMOC',
      descricao:
        'Documento central que rege a manutenção. Aplica planos NBR 17.037:2023 e gera ordens de serviço automaticamente.',
      ctaTo: '/pmoc/novo',
      ctaLabel: '+ Novo PMOC',
      feito: progresso.pmocs > 0,
    },
  ]
}
